import { ShieldCheck } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { Field, Notice } from '@/components/admin/primitives';
import { PageShell } from '@/components/common/PageShell';
import { DarkButton } from '@/components/ui/dark-button';
import { GlassCard } from '@/components/ui/glass-card';
import { useAuth } from '@/auth/auth-context';
import { safeNext } from '@/auth/RequireAuth';
import { loginErrorCopy } from '@/lib/admin-copy';

/**
 * The front door: what `video.theranow.com` shows anyone who is not signed in.
 *
 * Signing in does not navigate. The authed branch below does, so arriving by
 * redirect and arriving by typing `/login` end in the same place by the same
 * route.
 */
export function LoginPage() {
  const emailId = useId();
  const passwordId = useId();
  const location = useLocation();
  const { status, signIn, reason } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (status === 'authed') {
    return <Navigate to={safeNext((location.state as { from?: unknown })?.from)} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
    } catch (caught) {
      setError(loginErrorCopy(caught));
      setPassword('');
      setBusy(false);
    }
  }

  return (
    <PageShell>
      <GlassCard className="w-full max-w-md rounded-3xl p-8">
        <div
          className="mx-auto mb-4 grid size-12 place-items-center rounded-full bg-blue/20 text-[#3b9cff]"
          aria-hidden="true"
        >
          <ShieldCheck className="size-6" aria-hidden="true" />
        </div>
        <h2 className="text-center text-xl font-semibold tracking-tight text-call-ink">
          Sign in
        </h2>
        <p className="mt-2 text-center text-[0.9375rem] leading-relaxed text-call-dim">
          The operator console for the video platform.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
          <Field id={emailId} label="Email">
            <input
              id={emailId}
              type="email"
              autoComplete="username"
              autoFocus
              spellCheck={false}
              className="admin-field"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-invalid={error ? true : undefined}
            />
          </Field>
          <Field id={passwordId} label="Password">
            <input
              id={passwordId}
              type="password"
              autoComplete="current-password"
              className="admin-field"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-invalid={error ? true : undefined}
            />
          </Field>
          {reason && !error && <Notice tone="error">{reason}</Notice>}
          {error && <Notice tone="error">{error}</Notice>}
          <DarkButton type="submit" className="w-full" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </DarkButton>
        </form>
      </GlassCard>
    </PageShell>
  );
}
